import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuctionData } from 'src/app/modules/graphql/service/graphql-directory-service';
import { LotsListQuery } from 'src/app/modules/graphql/service/graphql-cars-default-service';
import { Lot, LotDataFragment } from 'src/app/modules/graphql/service/graphql-auto-main-service';
import { AuctionService } from 'src/app/services/auction.service';
import { LotWatchRequestTimeLimitService } from 'src/app/services/lot-watch-request-time-limit.service';

@Injectable()
export class AutoLotsListWatchRequestService {
  constructor(
    private auctionService: AuctionService,
    private lotWatchRequestTimeLimitService: LotWatchRequestTimeLimitService,
  ) {}

  getAvailableData(
    lots: LotsListQuery['lots']['nodes'],
    lotListData: Map<string, LotDataFragment>,
    lotInspectedData: Map<string, boolean>,
  ): Observable<Map<string, boolean>> {
    const auctionNames = this.getAuctionNames(lots, lotInspectedData);

    if (!auctionNames.length) {
      return of(new Map<string, boolean>());
    }

    return combineLatest(
      auctionNames.map((auctionName) =>
        this.auctionService.getByName(auctionName).pipe(
          map((auction) => ({
            auctionName,
            auctionData: auction?.auctionData,
          })),
        ),
      ),
    ).pipe(
      map((auctionItems) =>
        this.buildAvailableData(
          lots,
          lotListData,
          lotInspectedData,
          this.buildAuctionDataByName(auctionItems),
        ),
      ),
    );
  }

  private getAuctionNames(
    lots: LotsListQuery['lots']['nodes'],
    lotInspectedData: Map<string, boolean>,
  ): string[] {
    return Array.from(
      new Set(
        lots
          .filter((lot) => !lotInspectedData.get(String(lot.id)))
          .map((lot) => lot.autoAuction?.name)
          .filter((auctionName): auctionName is string => Boolean(auctionName)),
      ),
    );
  }

  private buildAuctionDataByName(
    auctionItems: Array<{ auctionName: string; auctionData?: AuctionData | null }>,
  ): Map<string, AuctionData> {
    return auctionItems.reduce((result, item) => {
      if (item.auctionData) {
        result.set(item.auctionName, item.auctionData);
      }

      return result;
    }, new Map<string, AuctionData>());
  }

  private buildAvailableData(
    lots: LotsListQuery['lots']['nodes'],
    lotListData: Map<string, LotDataFragment>,
    lotInspectedData: Map<string, boolean>,
    auctionDataByName: Map<string, AuctionData>,
  ): Map<string, boolean> {
    return lots.reduce((result, lot) => {
      const lotId = String(lot.id);
      const auctionData = lot.autoAuction?.name
        ? auctionDataByName.get(lot.autoAuction.name)
        : undefined;

      if (lotInspectedData.get(lotId)) {
        result.set(lotId, false);
      } else if (auctionData?.inspectionEnabled) {
        result.set(
          lotId,
          this.lotWatchRequestTimeLimitService.isInspectionAvailable(
            this.buildInspectionLot(lot, lotListData.get(lotId)),
            auctionData,
          ),
        );
      } else {
        result.set(lotId, false);
      }

      return result;
    }, new Map<string, boolean>());
  }

  private buildInspectionLot(
    lot: LotsListQuery['lots']['nodes'][number],
    lotData?: LotDataFragment,
  ): Lot {
    return {
      auctionDatetime: lot.time,
      currentUserLastBid: lotData?.currentUserLastBid
        ? {
            status: lotData?.currentUserLastBid?.status,
          }
        : null,
    } as Lot;
  }
}
